import { useContext, useEffect, useState } from 'react';

import { Grid } from '@mui/material';
import Swal from 'sweetalert2';

import { ReactComponent as Eye } from 'design_system/src/static/icons/eye.svg';
import { ReactComponent as Google } from 'design_system/src/static/icons/google.svg';
import { ReactComponent as Trash } from 'design_system/src/static/icons/trash.svg';

import '../../ozoner.scss';

import LoadDocuments from 'components/hocs/loadDocuments/loadDocuments';
import {
	Button,
	ModalConfirmation as Modal,
	Typography,
} from 'design_system/src';
import {
	deleteFileFinancialForm,
	deleteFileFullPaymentFinancialForm,
	uploadFileFinancialForm,
	uploadFileFullPaymentFinancialForm,
	uploadTaxSituationFinancialForm,
} from 'models/financial-form/controllers/financial-form.controller';
import { FinancialFormTypeEnum } from 'models/financial-form/enums/financial-form.enums';
import {
	deleteFileOzoner,
	uploadFileOzoner,
} from 'models/ozoner/controllers/ozoner.controller';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import {
	EvidenceDocument,
	EvidenceDocumentType,
	EvidenceDocumentTypeValues,
} from 'models/shared/types/documents';
import { ozonerContext } from '../OzonerContext';

const formatName = (name: string) => name.replaceAll(' ', '_');

function DocumentsOzoner() {
	// -----------------DATA STATES------------------- //
	const [comment, setComment] = useState<string>('');
	const [openFiles, setOpenFiles] = useState(false);
	const [pendingDelete, setPendingDelete] = useState<{
		file: { _id: string; typeEvidence: string };
		target: 'ozoner' | 'form';
	} | null>(null);

	const { ozoner, setOzoner, financialForm, updateFormData, updateComment } =
		useContext(ozonerContext);
	const [reloadDocuments, setReloadDocuments] = useState(false);
	useEffect(() => {
		setReloadDocuments(true);
		setTimeout(() => {
			setReloadDocuments(false);
		}, 50);
	}, [financialForm]);

	useEffect(() => {
		setComment(ozoner?.comment ?? '');
	}, [ozoner]);

	const deleteFiles = (file: { _id: string; typeEvidence: string }) => {
		if (ozoner) {
			deleteFileOzoner(ozoner._id, file._id)
				.then((newData) => {
					setOzoner({ ...ozoner, documents: newData.documents });
					toasterNotification({
						msg: 'Documento eliminado satisfactoriamente',
						style: 'light',
						toasterType: toasterOptions.success,
					});
				})
				.catch(commonErrorHandler);
		}
	};
	const deleteFilesForm = (file: { _id: string; typeEvidence: string }) => {
		if (ozoner && financialForm?.form) {
			(financialForm.form.type === FinancialFormTypeEnum.fullPayment
				? deleteFileFullPaymentFinancialForm
				: deleteFileFinancialForm)(financialForm.form._id, file._id)
				.then((newData) => {
					toasterNotification({
						msg: 'Documento eliminado satisfactoriamente',
						style: 'light',
						toasterType: toasterOptions.success,
					});
					updateFormData({
						...financialForm.form,
						documents: newData.documents,
					});
				})
				.catch(commonErrorHandler);
		}
	};

	const confirmDelete = () => {
		if (!pendingDelete) return;
		(pendingDelete.target === 'ozoner' ? deleteFiles : deleteFilesForm)(
			pendingDelete.file,
		);
		setPendingDelete(null);
	};

	const handleFile = (
		e: any,
		fileType: EvidenceDocumentType,
		isOzoner = true,
	) => {
		const file = e.target.files[0];
		if (
			file.size >
			(fileType === EvidenceDocumentType.videoEvidenceAddress ||
			fileType === EvidenceDocumentType.videoEvidenceWork
				? 30000000
				: 10000000)
		) {
			Swal.fire({
				text:
					fileType === EvidenceDocumentType.videoEvidenceAddress ||
					fileType === EvidenceDocumentType.videoEvidenceWork
						? 'El video no debe pesar mas de 30mb'
						: 'La imagen no debe pesar mas de 10mb',
				icon: 'warning',
				confirmButtonText: 'Aceptar',
			});
		} else if (ozoner) {
			const blob = file.slice(0, file.size, file.type);
			const newFile = new File([blob], formatName(file.name), {
				type: file.type,
			});
			if (isOzoner) {
				uploadFileOzoner(ozoner._id, newFile, fileType)
					.then((newData) => {
						setOzoner({
							...ozoner,
							documents: newData.documents,
						});
						toasterNotification({
							msg: 'Documento cargado satisfactoriamente',
							style: 'light',
							toasterType: toasterOptions.success,
						});
					})
					.catch(commonErrorHandler);
			} else if (fileType === EvidenceDocumentType.tax_situation) {
				const previousTaxDataUpdatedAt = (financialForm.form as any)?.taxData
					?.updatedAt;
				toasterNotification({
					msg: 'Subiendo documento y extrayendo datos fiscales, espera un momento...',
					style: 'light',
					toasterType: toasterOptions.info,
				});
				uploadTaxSituationFinancialForm(financialForm.form._id, newFile)
					.then((newData) => {
						const taxDataWasUpdated =
							!!newData.taxData?.updatedAt &&
							newData.taxData.updatedAt !== previousTaxDataUpdatedAt;
						updateFormData({
							...(financialForm.form as any),
							documents: newData.documents,
							taxData: newData.taxData,
						});
						toasterNotification({
							msg: taxDataWasUpdated
								? 'Documento y datos fiscales cargados satisfactoriamente'
								: 'Documento cargado satisfactoriamente, no se pudieron extraer los datos fiscales',
							style: 'light',
							toasterType: taxDataWasUpdated
								? toasterOptions.success
								: toasterOptions.warning,
						});
					})
					.catch(commonErrorHandler);
			} else {
				(financialForm.form.type === FinancialFormTypeEnum.fullPayment
					? uploadFileFullPaymentFinancialForm
					: uploadFileFinancialForm)(
					financialForm?.form?._id,
					newFile,
					fileType,
				)
					.then((newData) => {
						updateFormData({
							...financialForm.form,
							documents: newData.documents,
						});
						toasterNotification({
							msg: 'Documento cargado satisfactoriamente',
							style: 'light',
							toasterType: toasterOptions.success,
						});
					})
					.catch(commonErrorHandler);
			}
		}
	};

	const openDrive = () => {
		window.open(process.env.REACT_APP_DOCUMENTS_DRIVE, '_blank');
	};

	return (
		<>
			<div className="br_xxs bg_neutral_0 p_xs border_1 border_solid border_neutral_400 m_b_md">
				<div className="bg_neutral_100 br_xxs p_xs border_1 border_solid border_neutral_300">
					<div className="display_flex flex_justify_between flex_align_center">
						<Typography
							scale="medium"
							weight="600"
							textColor="neutral_900"
							className="m_y_md"
						>
							Documentación del Ozoner
						</Typography>
					</div>

					<Grid
						container
						direction="row"
						spacing={1}
						className="m_none w_100_per"
					>
						<LoadDocuments
							documents={ozoner?.documents ?? []}
							onDelete={(file: EvidenceDocument<EvidenceDocumentType>) =>
								setPendingDelete({ file, target: 'ozoner' })
							}
							onAdd={handleFile}
							availableDocs={[
								EvidenceDocumentType.selfie,
								EvidenceDocumentType.ine_front,
								EvidenceDocumentType.ine_back,
								EvidenceDocumentType.license_front,
								EvidenceDocumentType.license_back,
								EvidenceDocumentType.proof_of_address,
								EvidenceDocumentType.videoEvidenceAddress,
								EvidenceDocumentType.videoEvidenceWork,
								EvidenceDocumentType.truora,
							]}
							docsValues={EvidenceDocumentTypeValues}
						/>
					</Grid>
					<Typography
						scale="medium"
						weight="600"
						textColor="neutral_900"
						className="m_y_md"
					>
						Documentación del Crédito
					</Typography>

					<Grid
						container
						direction="row"
						spacing={1}
						className="m_none w_100_per"
					>
						{!reloadDocuments ? (
							<LoadDocuments
								documents={[
									...(financialForm?.form?.documents ?? []),
									...(financialForm?.loan?.documents ?? []),
								]}
								onDelete={(file: any) =>
									setPendingDelete({ file, target: 'form' })
								}
								onAdd={(e: any, type: EvidenceDocumentType) =>
									handleFile(e, type, false)
								}
								docsValues={EvidenceDocumentTypeValues}
								availableDocs={
									// eslint-disable-next-line no-nested-ternary
									financialForm.form.type === FinancialFormTypeEnum.loan
										? [
												EvidenceDocumentType.bank_certificate_1,
												EvidenceDocumentType.bank_certificate_2,
												EvidenceDocumentType.bank_certificate_3,
												EvidenceDocumentType.bank_certificate_4,
												EvidenceDocumentType.bank_certificate_5,
												EvidenceDocumentType.bank_certificate_6,
												EvidenceDocumentType.downpayment,
												EvidenceDocumentType.contract,
												EvidenceDocumentType.layawayReceipt,
												EvidenceDocumentType.promissory_note,
												EvidenceDocumentType.delivery_photo,
												EvidenceDocumentType.delivery_certificate,
												EvidenceDocumentType.insurance_policy,
												EvidenceDocumentType.tax_situation,
										  ]
										: financialForm.form.type ===
										  FinancialFormTypeEnum.creditLine
										? [
												EvidenceDocumentType.contract,
												EvidenceDocumentType.promissory_note,
												EvidenceDocumentType.delivery_photo,
												EvidenceDocumentType.delivery_certificate,
												EvidenceDocumentType.insurance_policy,
												EvidenceDocumentType.tax_situation,
										  ]
										: [
												EvidenceDocumentType.contract,
												EvidenceDocumentType.delivery_photo,
												EvidenceDocumentType.delivery_certificate,
												EvidenceDocumentType.insurance_policy,
										  ]
								}
							/>
						) : null}
					</Grid>
				</div>
			</div>
			<div className="bg_neutral_0 br_xs border_1 border_solid border_neutral_400 p_x_lg p_y_sm">
				<Typography
					scale="large"
					weight="600"
					textColor="neutral_900"
					className="m_y_md"
				>
					Observaciones del ozoner :
				</Typography>
				<textarea
					className="observaciones w_100_per p_md border_1 border_solid border_neutral_400"
					placeholder="Agregue todas las observaciones del ozoner que se detecten"
					value={comment}
					onChange={(e) => setComment(e.target.value)}
				/>
				{comment !== ozoner?.comment && (
					<div className="display_flex flex_justify_end">
						<Button
							scale="small"
							variant="ghost"
							onClick={() => updateComment(comment)}
						>
							Guardar Cambios
						</Button>
						<Button
							scale="small"
							variant="ghost"
							onClick={() => setComment(ozoner?.comment ?? '')}
						>
							Cancelar
						</Button>
					</div>
				)}
			</div>
			{openFiles && (
				<Modal
					openConfirmation={openFiles}
					handleClose={() => setOpenFiles(false)}
					icon={<Eye className="text_primary_300" />}
					widthDesktop={40}
					isConfirmationLoading={false}
					actions={[
						{
							label: 'Salir',
							color: 'grey',
							action: () => setOpenFiles(false),
						},
					]}
				>
					<>
						<Typography scale="large" weight="600" className="m_b_xs">
							Ver documentación{' '}
						</Typography>
						<Typography
							scale="medium"
							weight="400"
							className="m_b_md"
							textColor="neutral_700"
						>
							Los documentos documentos fueron generados con exito
						</Typography>
						<Button
							scale="small"
							onClick={openDrive}
							icon={<Google />}
							className="w_100_per"
						>
							Ver documentos en Google Drive
						</Button>
					</>
				</Modal>
			)}
			{pendingDelete && (
				<Modal
					openConfirmation={!!pendingDelete}
					handleClose={() => setPendingDelete(null)}
					icon={<Trash className="text_red_300" />}
					widthDesktop={35}
					isConfirmationLoading={false}
					actions={[
						{
							label: 'Cancelar',
							color: 'grey',
							action: () => setPendingDelete(null),
						},
						{
							label: 'Eliminar',
							color: 'red',
							action: confirmDelete,
						},
					]}
				>
					<>
						<Typography scale="large" weight="600" className="m_b_xs">
							Eliminar documento
						</Typography>
						<Typography
							scale="medium"
							weight="400"
							className="m_b_md"
							textColor="neutral_700"
						>
							¿Seguro que deseas eliminar este documento? Esta acción no se
							puede deshacer.
						</Typography>
					</>
				</Modal>
			)}
		</>
	);
}
export default DocumentsOzoner;
